import {
  Box,
  Flex,
  MultiSelect,
  Select,
  Space,
  Stack,
  Text,
  TextInput,
  Title,
  Button,
  Card,
} from "@mantine/core";
import { FC, useState } from "react";
import {
  GetManyOrdersOfUserDocument,
  GetManyOrdersOfUserQuery,
  OrderStatus,
  SortOrder,
  NullsOrder,
} from "/@/generated/graphql";
import { useAuthContext } from "/@/shared/contexts/AuthContext";
import { useQuery } from "@apollo/client";
import { OrderSummaryRow } from "/@/components/order/OrderSummaryRow";
import { ConfirmedOrderStatusLabels } from "/@/shared/types/orders/order.types";
import { useDebounce } from "use-debounce";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { OrdersListPageV2 } from "/@/pages/orders/OrdersListPageV2.tsx";

const _TAKE_ = 5;

const OrdersListPageV1: FC = () => {
  const [skip, setSkip] = useState<number>(0);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState<boolean>(false);
  const [displayedOrders, setDisplayedOrders] = useState<
    Array<NonNullable<GetManyOrdersOfUserQuery["getManyOrdersOfUser"]>[0]>
  >([]);
  const [selectedDentist, setSelectedDentist] = useState<string | null>(null);
  const [selectedOrderStatuses, setSelectedOrderStatuses] = useState<
    OrderStatus[]
  >([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchInputDebounce] = useDebounce(searchInput, 500);

  const { user } = useAuthContext();

  const searchFilters = searchInputDebounce
    .trim()
    .split(" ")
    .filter(Boolean)
    .reduce((acc: any[], word: string) => {
      acc.push(
        ...[
          {
            patient: {
              is: {
                firstName: {
                  contains: word,
                  mode: "insensitive",
                },
              },
            },
          },
          {
            patient: {
              is: {
                lastName: {
                  contains: word,
                  mode: "insensitive",
                },
              },
            },
          },
          {
            orderReference: {
              contains: word,
              mode: "insensitive",
            },
          },
        ]
      );
      return acc;
    }, [] as any[]);
  const { data, loading } = useQuery(GetManyOrdersOfUserDocument, {
    fetchPolicy: "network-only",
    variables: {
      take: _TAKE_,
      skip: skip,
      where: {
        userId: selectedDentist
          ? {
              equals: selectedDentist,
            }
          : undefined,
        status:
          selectedOrderStatuses.length === 0
            ? {
                not: {
                  equals: OrderStatus.Draft,
                },
              }
            : {
                in: selectedOrderStatuses,
              },
        AND:
          searchFilters.length > 0
            ? [
                {
                  OR: searchFilters,
                },
              ]
            : undefined,
      },
      orderBy: [
        {
          confirmedAt: {
            nulls: NullsOrder.Last,
            sort: SortOrder.Desc,
          },
        },
        {
          updatedAt: SortOrder.Desc,
        },
      ],
    },
    onCompleted: data => {
      if (
        (data?.getManyOrdersOfUser ?? []).length > 0 &&
        data?.getManyOrdersOfUser.length % _TAKE_ === 0
      ) {
        setShowLoadMoreButton(true);
      } else {
        setShowLoadMoreButton(false);
      }
      setDisplayedOrders([...(data?.getManyOrdersOfUser ?? [])]);
    },
  });

  if (
    (data?.getManyOrdersOfUser ?? []).length === 0 &&
    searchInputDebounce.length === 0 &&
    displayedOrders.length === 0 &&
    !searchInput.trim() &&
    !selectedDentist &&
    selectedOrderStatuses.length === 0 &&
    !loading
  ) {
    return (
      <Stack gap={0} justify="center" align="center" h="100%" bg="greyScale.9">
        <Title order={3} c="greyScale.2">
          {"Vous n'avez pas encore passé commande"}
        </Title>
        <Text c="greyScale.2">
          {"Veuillez passer une commande via votre caméra intra-orale 3Shape"}
        </Text>
        <Text mb="10%" c="greyScale.2">
          {"ou via le formulaire de commande en haut à droite de l'écran"}
        </Text>
      </Stack>
    );
  }

  return (
    <Flex
      p="lg"
      h="calc(100vh - 100px)"
      direction="column"
      justify="space-between"
    >
      <Box>
        <Title order={2} c="greyScale.0" fw={700} fz="1.5rem">
          Commandes
        </Title>
        <Space h="sm" />
        <Flex gap="md">
          {user?.userGroup?.id && (
            <Select
              maw="40%"
              miw="30%"
              label="Afficher les commandes de"
              defaultValue={""}
              data={[
                {
                  value: "",
                  label: "Tous",
                },
                ...(user?.userGroup?.groupMembers ?? [])
                  .filter(dentist => !dentist.isManager)
                  .map(dentist => ({
                    value: dentist.id,
                    label: `Dr. ${dentist.firstName} ${dentist.lastName}`,
                  })),
              ]}
              onChange={value => {
                setDisplayedOrders([]);
                setSkip(0);
                setSelectedDentist(value || null);
              }}
            />
          )}
          <MultiSelect
            maw="40%"
            miw="30%"
            clearable
            label="Filtrer par statut"
            placeholder="Aucun"
            data={ConfirmedOrderStatusLabels}
            onChange={value => {
              setDisplayedOrders([]);
              setSkip(0);
              setSelectedOrderStatuses(value as OrderStatus[]);
            }}
          />
          <TextInput
            flex="1 1 auto"
            placeholder="Tous"
            label="Chercher une référence ou un patient"
            value={searchInput}
            onChange={event => {
              if (searchInput !== event.currentTarget.value.trim()) {
                setDisplayedOrders([]);
                setSkip(0);
              }
              setSearchInput(event.currentTarget.value);
            }}
          />
        </Flex>
        <Space h="md" />
        <Box w="100%">
          <Space h="md" />
          {displayedOrders.length > 0 ? (
            <Card
              radius="lg"
              bg="white"
              bd="1px solid greyScale.8"
              px="md"
              py={0}
            >
              {displayedOrders.map((order, index) => (
                <OrderSummaryRow
                  key={order.id}
                  order={order}
                  variant="compact"
                  isLast={index === displayedOrders.length - 1}
                />
              ))}
            </Card>
          ) : (
            <Card radius="lg" bg="white" bd="1px solid greyScale.8" p="xl">
              <Text ta="center" c="dimmed">
                Aucune commande à afficher
              </Text>
            </Card>
          )}
        </Box>
      </Box>
      <Flex justify="space-between" mt="xs">
        {displayedOrders.length === 0 &&
        searchInput &&
        searchInputDebounce !== searchInput ? (
          <Text>Chargement...</Text>
        ) : (
          <>
            {!loading && skip !== 0 ? (
              <Button onClick={() => setSkip(skip - _TAKE_)}>Précédent</Button>
            ) : (
              <Box />
            )}
            {!loading && showLoadMoreButton ? (
              <Button onClick={() => setSkip(skip + _TAKE_)}>Suivant</Button>
            ) : (
              <Box />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};

export const OrdersListPage: FC = () => {
  const isThemeV2Enabled = useFeatureFlagEnabled("theme_v2", false);
  return isThemeV2Enabled ? <OrdersListPageV2 /> : <OrdersListPageV1 />;
};
